import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  Header,
} from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef} from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';

// Material UI components
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const RDSSnapshot = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);

  const [rowData, setRowdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');
  const [tenantName, settenantName] = useState<string>(''); // dropdown state
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);
  const identityApi = useApi(identityApiRef)
  
  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenants);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let data: any[] = [];
        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/rdssnapshots`, {
            method: 'POST',
          });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          const response = await fetchresponse.json();
          data = response.data || response; // adjust based on backend response
          setRole(response.role || ''); // ensure role comes from backend
          console.log('Fetched RDS data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }
        const filterData = data.filter(item => item.totalCost !== 0);
        setRowdata(filterData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchData();
    const orgName = localStorage.getItem('orgName');
    if (orgName) {
      setOrg(orgName);
    }
  }, [discoveryApi, fetchApi]);

  //  Filter data based on tenant selection
  const filteredData = tenantName
    ? rowData.filter(row => String(row.tenantName) === String(tenantName))
    : rowData;

  //  Unique tenants for dropdown
  const tenants = [...new Set(rowData.map(r => r.tenantName))];

  const dateFormatter = (params: any) => {
    if (!params.node.group) {
      const dateParts = params.value.split('T'); // Split at the 'T' character
      return dateParts[0];
    }
  }

  const columnDefs = useMemo(() => {
    const baseCols = [
    {
      headerName: "Account Id", field: "accountId", headerTooltip: "Account Id", rowGroup: true, valueFormatter: params => {
        if (params.node.rowIndex === params.api.getDisplayedRowCount() - 1 && !params.value) {
          return 'Total';
        }
        else {
          return params.value;
        }
      }
    },
    { headerName: "Region", field: "region", headerTooltip: "Region" },
    { headerName: "Snapshot Id", field: "snapshotId", headerTooltip: "Snapshot Id" },
    { headerName: "Creation Date", field: "creationTime", headerTooltip: "Creation Date", valueFormatter: dateFormatter },
    { headerName: "Allocated Storage(GB)", field: "allocatedStorageGB", headerTooltip: "Allocated StorageGB" },
    { headerName: "Storage Type", field: "storageType", headerTooltip: "Storage Type" },
    { headerName: "Snapshot Type", field: "snapshotType", headerTooltip: "Snapshot Type" },
    {
      headerName: "Recommendations", field: "recommendations", headerTooltip: "Recommendations", valueFormatter: params => {
        try {
          const recommendations = JSON.parse(params.value);
          return Array.isArray(recommendations) ? recommendations.join(', ') : params.value;
        } catch (e) {
          console.error('Error parsing recommendations:', e);
          return params.value;
        }
      }
    },
    {
      headerName: "Snapshot Cost in $(per GB per month)", field: "snapshotCost", headerTooltip: "Snapshot Cost", aggFunc: 'sum', valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value))
          return value.toFixed(2);
      },
    }
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1, filter: true, sortable: true, resizable: true, enableRowGroup: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
    };
  }, []);

  const autoGroupColumnDef = {
    minWidth: 250,
    cellRendererParams: {
      footerValueGetter: (params) => {
        const isRootLevel = params.node.level === -1;
        if (isRootLevel) {
          return "Total";
        }
        return " Sub Total";
      },
    },
  }

  const sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      }
    ],
    defaultToolPanel: '',
  };

  const gridOptions = { suppressContextMenu: true };

  return (
    <div style={containerStyle}>
      <div>
        <Header title="RDS-Snapshot" subtitle={`${org}`} />
      </div>

      {/* Tenant Dropdown */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel></InputLabel>
            <Select
              value={tenantName}
              onChange={(e) => settenantName(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    // force vertical stacking
                    '& .MuiList-root': {
                      display: 'flex !important',
                      flexDirection: 'column !important',
                    },
                    // force left-aligned text inside items
                    '& .MuiMenuItem-root': {
                      justifyContent: 'flex-start',
                      pl: 2,
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All Tenants</MenuItem>
              {tenantsFromDb.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.name}>
                {tenant.name}
              </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {loading ? (<Loader />) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowGroupPanelShow={"always"}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              groupIncludeFooter={true}
              groupIncludeTotalFooter={true}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div className="scroll-container">
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Snapshot Retention and Deletion</strong>
                  <p>Managing RDS snapshots effectively can help optimize storage costs. The following steps highlight actions you can take in the AWS console to align with the programmatic analysis performed by your script:</p>
                  <ul>
                    <li>Ensure automated snapshots' retention does not exceed 7 days, and delete manual snapshots older than 30 days.</li>
                    <li>Consider reducing automated snapshot frequency for lower retention.</li>
                    <li>Delete unused snapshots to reduce storage costs.</li>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Regularly review snapshot usage and retention policies to align with cost-saving strategies.</li>
                    <li>Use the RDS console to adjust backup configurations and implement snapshot lifecycle management.</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: The potential cost savings for RDS snapshots are calculated based on their storage costs. Each snapshot's cost is determined by multiplying its allocated storage (in GB) by the region-specific cost rate for General Purpose SSD storage. The potential savings are assessed by comparing the active storage cost with the archival cost, which is lower. If a snapshot is deleted rather than archived, the savings equal the full active storage cost.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RDSSnapshot;
